/**
 * N1 — Sélecteur d'archétype initial.
 *
 * S'affiche au boot d'une nouvelle session (pas de save compatible).
 * Le joueur choisit le contexte historique de son mandat parmi 3
 * variantes. Le choix est persisté en localStorage et lu par le
 * boot du store. Au reset, l'overlay revient.
 */

import { useEffect, useState } from "react";

export const ARCHETYPE_CHOICE_KEY = "mp.archetypeChoice";

export interface ArchetypeChoice {
  id: string;
  file: string;
  label: string;
  pitch: string;
  details: string[];
}

export const ARCHETYPES: ArchetypeChoice[] = [
  {
    id: "social_cradle",
    file: "/data/archetypes/social_cradle.json",
    label: "Le Berceau social",
    pitch: "Démocratie sociale mature, dette élevée, équilibre fragile.",
    details: [
      "Dette publique ~110 % du PIB",
      "Chômage 8.7 %, inflation 2.1 %",
      "Protection sociale solide, vieillissement visible",
    ],
  },
  {
    id: "france_liberale_2030",
    file: "/data/archetypes/france_liberale_2030.json",
    label: "Hexagone libéral 2030",
    pitch: "Marché flexibilisé, dette maîtrisée, inégalités creusées.",
    details: [
      "Dette publique ~95 %, currency 96",
      "Chômage 6.5 %, inflation 1.8 %",
      "Élites satisfaites, périphérie en désengagement",
    ],
  },
  {
    id: "france_post_choc",
    file: "/data/archetypes/france_post_choc.json",
    label: "Hexagone post-choc",
    pitch: "Économie de guerre, dette explosée, monnaie sous pression.",
    details: [
      "Dette publique ~130 %, currency 78",
      "Chômage 11 %, inflation 4.2 %",
      "Militarisation forcée, alliances contraintes",
    ],
  },
];

export function loadArchetypeChoice(): ArchetypeChoice {
  try {
    const raw = localStorage.getItem(ARCHETYPE_CHOICE_KEY);
    if (raw) {
      const found = ARCHETYPES.find((a) => a.id === raw);
      if (found) return found;
    }
  } catch {
    /* quota / private mode */
  }
  return ARCHETYPES[0];
}

export function persistArchetypeChoice(id: string): void {
  try {
    localStorage.setItem(ARCHETYPE_CHOICE_KEY, id);
  } catch {
    /* quota */
  }
}

interface Props {
  onSelect: (choice: ArchetypeChoice) => void;
}

export function ArchetypeSelector({ onSelect }: Props) {
  const [picked, setPicked] = useState<string | null>(null);
  // Écran d'introduction affiché AVANT le contexte initial (remplace la
  // cinématique d'investiture, désormais redondante).
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    // Pre-select le choix existant si on revient sur l'écran.
    try {
      const raw = localStorage.getItem(ARCHETYPE_CHOICE_KEY);
      if (raw) setPicked(raw);
    } catch {
      /* ignore */
    }
  }, []);

  const handleConfirm = () => {
    if (!picked) return;
    const choice = ARCHETYPES.find((a) => a.id === picked);
    if (!choice) return;
    persistArchetypeChoice(picked);
    onSelect(choice);
  };

  if (showIntro) {
    return (
      <div className="archetype-backdrop archetype-backdrop-intro">
        <div
          className="archetype-card archetype-intro-card"
          role="dialog"
          aria-modal="true"
          aria-label="Entrée en fonction"
        >
          <div className="archetype-pretitle">MAI 2028 · Ve RÉPUBLIQUE</div>
          <h2 className="archetype-title">Vous venez d'être élu.</h2>
          <p className="archetype-intro archetype-intro-lead">
            À vous d'écrire l'histoire du pays.
          </p>
          <button
            type="button"
            className="archetype-confirm"
            onClick={() => setShowIntro(false)}
          >
            Entrer en fonction →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="archetype-backdrop">
      <div
        className="archetype-card"
        role="dialog"
        aria-modal="true"
        aria-label="Sélection d'archétype initial"
      >
        <div className="archetype-pretitle">CONTEXTE INITIAL</div>
        <h2 className="archetype-title">De quel état du pays héritez-vous ?</h2>
        <p className="archetype-intro">
          Trois points de départ, trois rapports de force. Votre choix oriente
          l'état initial des cohortes, de l'économie et des tensions
          internationales — tout le reste émerge de vos décisions.
        </p>

        <ol className="archetype-options">
          {ARCHETYPES.map((a) => (
            <li
              key={a.id}
              className={`archetype-option ${picked === a.id ? "is-picked" : ""}`}
            >
              <button
                type="button"
                className="archetype-option-btn"
                aria-pressed={picked === a.id}
                onClick={() => setPicked(a.id)}
              >
                <div className="archetype-option-label">{a.label}</div>
                <div className="archetype-option-pitch">{a.pitch}</div>
                <ul className="archetype-option-details">
                  {a.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </button>
            </li>
          ))}
        </ol>

        <button
          type="button"
          className="archetype-confirm"
          onClick={handleConfirm}
          disabled={!picked}
        >
          ━━ COMMENCER LE MANDAT ━━
        </button>
      </div>
    </div>
  );
}
